import moment from 'moment';
import { findBy$IdInTreeList, getTopOfTree } from './tree';
import { hasGlobalPriority } from '../models/todo';
import { TodoType } from '../models/todo-type';
import { getTimelineTodoList } from '../store/selector';

export const TOP_PRIORITY_COUNT = 3;
export const NEXT_MOST_PRIORITY_COUNT = 5;

export function filterOverview(todos, filter, now) {

    if (!todos) return { projects: [], tasks: [] };
    try {
        const filteredItems = todos
            .filter(item => canSeeCompleted(item, filter))
            .filter(item => canSeeHidden(item, filter, now))
            .filter(item => filter
                ? !(isProjectAncestorHiddenByFilter(item, filter.CannotSeeProjectIds) ||
                    isProjectParentHiddenByFilter(item, filter.CannotSeeProjectIds))
                : true);
        const projects = filteredItems
            .filter(item => item.type === TodoType.project)
            .sort(sortBySiblingOrder);
        const tasks = filteredItems
            .filter(item => item.type === TodoType.task)
            .sort(sortByGlobalPriority);
        return {
            projects: filterList(projects, item => canSeeCompleted(item, filter) && canSeeHidden(item, filter, now)),
            tasks: filterList(tasks, item => canSeeCompleted(item, filter) && canSeeHidden(item, filter, now))
        };
    } catch (error) {
        console.error('Error in filterOverview', error);
        return { projects: [], tasks: [] };
    }
}

export function sortBySiblingOrder(t1, t2) {
    return t1.siblingOrder - t2.siblingOrder;
}

export function sortByGlobalPriority(t1, t2) {
    if (!hasGlobalPriority(t1)) {
        if (!hasGlobalPriority(t2)) {
            return sortById(t1, t2);
        } else {
            return 1;
        }
    } else if (!hasGlobalPriority(t2)) {
        return -1;
    } else {
        return t1.globalPriority - t2.globalPriority;
    }
}

export function isNullOrUndefined(value) {
    return value === undefined || value === null;
}

export function filterList(todos, include) {
    const ret = [];
    for (const todo of todos) {
        const match = filterNode(todo, include);
        if (match) {
            ret.push(match);
        }
    }
    return ret;
}

function filterNode(todo, include) {
    return include(todo) ? { ...todo, children: filterList(todo.children, include) } : null;
}

export const canSeeCompleted = (todo, filter) => filter && !filter.CanSeeCompleted ? !todo.isCompleted : true;
export const canSeeHidden = (todo, filter, now) =>
    filter && !filter.CanSeeHidden ? !isSelfOrAncestorSnoozed(todo, now) : true;

export function isSelfOrAncestorSnoozed(item, now) {
    return isSnoozed(item, now) || (item.parent && isSelfOrAncestorSnoozed(item.parent, now));
}

export function isSnoozed(item, now) {
    return item.isSnoozed && isValidSnoozedDate(item.snoozedUntil, now);
}

export function isValidSnoozedDate(date, now) {
    if (!date) return false;

    const today = moment(now);
    return moment(date).isAfter(today);
}

export function isProjectAncestorHiddenByFilter(item, cannotSeeProjectIds) {
    let toptree = getTopOfTree(item);
    return isProjectHiddenByFilter(toptree, cannotSeeProjectIds);
}

export function isProjectParentHiddenByFilter(item, cannotSeeProjectIds) {
    if (item.parent !== null) {
        let parent = findBy$IdInTreeList(item.parent.$ref);
        return parent && isProjectHiddenByFilter(parent, cannotSeeProjectIds);
    }
    else {
        return false;
    }
}

export function isProjectHiddenByFilter(item, cannotSeeProjectIds) {
    return cannotSeeProjectIds &&
        cannotSeeProjectIds.length &&
        cannotSeeProjectIds.find(id => item.type === TodoType.project && id === item.id) ? true : false;
}

export function createGuid() {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    }
    return (s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4());
}

export function filterUnPrioritizedTasks(tasks, filter, now) {
    try {
        return tasks
            .filter(task => !task.isCompleted)
            .filter(task => canSeeHidden(task, filter, now))
            .filter(task => filter ? !isProjectAncestorHiddenByFilter(task, filter.CannotSeeProjectIds) : true)
            .filter(task => !hasGlobalPriority(task))
            .sort(sortById);
    } catch (error) {
        console.error('Error in filterUnPrioritizedTasks', error);
        return { tasks: [] };
    }
}

export function filterPriorityTaskList(tasks, filter, now) {
    if (!tasks) return {
        unPrioritizedTasks: [],
        mostPrioritizedTasks: [],
        nextMostPrioritizedTasks: [],
        otherTasks: [],
        completedTasks: []
    };
    try {
        const completedFilteredItems = tasks
            .filter(task => task.isCompleted)
            .filter(task => canSeeHidden(task, filter, now))
            .filter(task => filter ? !isProjectAncestorHiddenByFilter(task, filter.CannotSeeProjectIds) : true);

        const notCompletedItems = tasks
            .filter(task => !task.isCompleted);

        const notCompletedFilteredItems = notCompletedItems
            .filter(task => canSeeHidden(task, filter, now))
            .filter(task => filter ? !isProjectAncestorHiddenByFilter(task, filter.CannotSeeProjectIds) : true);

        const prioritizedTasks = notCompletedFilteredItems
            //.filter(task => hasGlobalPriority(task))
            .sort(sortByGlobalPriority);

        return {
            unPrioritizedTasks: filterUnPrioritizedTasks(tasks, filter, now),
            mostPrioritizedTasks: prioritizedTasks.slice(0, TOP_PRIORITY_COUNT),
            nextMostPrioritizedTasks: prioritizedTasks.slice(
                TOP_PRIORITY_COUNT,
                TOP_PRIORITY_COUNT + NEXT_MOST_PRIORITY_COUNT
            ),
            otherTasks: prioritizedTasks.slice(8),
            completedTasks: completedFilteredItems
        };
    } catch (error) {
        console.error('Error in filterPriorityTaskList', error);
        return {
            unPrioritizedTasks: [],
            mostPrioritizedTasks: [],
            nextMostPrioritizedTasks: [],
            otherTasks: [],
            completedTasks: []
        };
    }
}

export function sortByDueDate(t1, t2) {
    if (!t1.dueDate) {
        if (!t2.dueDate) {
            return 0;
        } else {
            return 1;
        }
    } else if (!t2.dueDate) {
        return -1;
    } else {
        return new Date(t1.dueDate).getTime() - new Date(t2.dueDate).getTime();
    }
}

export function filterTimelineTodoList(todos, filter, now) {
    if (!todos) {
        return {
            todaysTodos: [],
            tomorrowsTodos: [],
            thisWeeksTodos: [],
            nextWeeksTodos: [],
            tasksWithNoDueDate: [],
            projectsWithNoDueDate: [],
            overdueTodos: []
        };
    }
    const today = moment(now).startOf('day');
    const tomorrow = today.clone().add(1, 'days');

    try {
        const filteredItems = todos
            .filter(item => canSeeCompleted(item, filter))
            .filter(item => canSeeHidden(item, filter, now))
            .filter(item => filter
                ? !(isProjectAncestorHiddenByFilter(item, filter.CannotSeeProjectIds) ||
                    isProjectParentHiddenByFilter(item, filter.CannotSeeProjectIds))
                : true);

        return {
            todaysTodos: filteredItems
                .filter(todo => todo.dueDate && moment(todo.dueDate).isSame(today, 'day'))
                .sort(sortByDueDate),
            tomorrowsTodos: filteredItems
                .filter(todo => todo.dueDate && moment(todo.dueDate).isSame(tomorrow, 'day'))
                .sort(sortByDueDate),
            thisWeeksTodos: filteredItems
                .filter(todo => {
                    if (!todo.dueDate) { return false; }
                    const first = tomorrow.clone().add(1, 'days');
                    const last = today.clone().weekday(7).endOf('day');
                    return moment(todo.dueDate).isBetween(first, last, null, '[]');
                })
                .sort(sortByDueDate),
            nextWeeksTodos: filteredItems
                .filter(todo => {
                    if (!todo.dueDate) { return false; }
                    const first = today.clone().weekday(7).add(1, 'days');
                    const last = first.clone().weekday(7).endOf('day');
                    return moment(todo.dueDate).isBetween(first, last, null, '[]');
                })
                .sort(sortByDueDate),
            tasksWithNoDueDate: filteredItems
                .filter(todo => !todo.dueDate && todo.type === TodoType.task),
            projectsWithNoDueDate: filteredItems
                .filter(todo => !todo.dueDate && todo.type === TodoType.project),
            overdueTodos: filteredItems
                .filter(todo => todo.dueDate && moment(todo.dueDate).isBefore(today, 'day') && !todo.isCompleted)
                .sort(sortByDueDate)
        };
    } catch (error) {
        console.error('Error in filterTimelineTodoList', error);
        return {
            todaysTodos: [],
            tomorrowsTodos: [],
            thisWeeksTodos: [],
            nextWeeksTodos: [],
            tasksWithNoDueDate: [],
            projectsWithNoDueDate: [],
            overdueTodos: []
        };
    }
}

export function sortById(t1, t2) {
    if (t1.id < t2.id) {
        return -1;
    } else if (t1.id > t2.id) {
        return 1;
    } else {
        return 0;
    }
}

export function filterSearchTodoList(filteredTodo, filter, now) {
    if (filter.Text.trim() == "" || !filteredTodo) {
        return {
            tasks: [],
            projects: []
        };
    }
    try {
        const searchtext = filter.Text.trim();
        const filteredItems = filteredTodo
            .filter((item) => canSeeCompleted(item, filter))
            .filter((item) => canSeeHidden(item, filter, now))
            .filter((item) => filter
                ? !(isProjectAncestorHiddenByFilter(item, filter.CannotSeeProjectIds) ||
                    isProjectParentHiddenByFilter(item, filter.CannotSeeProjectIds))
                : true)
            .filter((item) => {
                return filter && searchtext
                    ? (item.title && item.title.toLowerCase().includes(searchtext.toLowerCase())) ||
                    (item.description && item.description.toLowerCase().includes(filter.Text.toLowerCase()))
                    : true;
            });

        return {
            tasks: filteredItems.filter(todo => todo.type === 0 || (todo.type === 1 && todo.parent !== null)),
            projects: filteredItems.filter(todo => todo.type === 1 && todo.parent === null)
        };
    } catch (error) {
        console.error('Error in filterSearchTodoList', error);
        return {
            tasks: [],
            projects: []
        };
    }
}